import { Button, Flex, Text, useTheme } from "@repo/ui";
import { tag } from "@repo/utils/typeUtils";
import { Toaster } from "sonner";
import {
	DefaultParams,
	PathPattern,
	Redirect,
	Route,
	RouteProps,
	Router,
	Switch,
} from "wouter";
import { Agentation } from "agentation";
import { AdminOnly } from "./components/AdminOnly";
import { AssistantPane } from "./components/assistant/AssistantPane";
import { LeftPane } from "./components/LeftPane";
import { Providers } from "./components/Providers";
import { SyncErrorHandler } from "./components/SyncErrorHandler";
import { TaskDetailsPane } from "./components/TaskDetailsPane";
import { useLoggedInUser } from "./contexts/AuthContext";
import { Routes, RoutePattern } from "./routing";
import { AdminRoute } from "./routes/admin";
import { ErrorPage } from "./routes/error";
import { Login } from "./routes/login";
import { TrainingGuideRoute } from "./routes/trainingGuide";
import { UIKitRoute } from "./routes/uikit";
import {
	getBasePath,
	getTenantSlug,
	TENANT_DISPLAY_NAMES,
	tenantPickerOrder,
	urlForTenantSlug,
} from "./tenant";
import { DemoControlBar } from "./components/DemoControlBar";

function ThemedToaster() {
	const theme = useTheme();

	return <Toaster position="bottom-right" theme={theme.name} />;
}

function Index() {
	const user = useLoggedInUser();

	if (user) {
		return <Redirect to={Routes.taskList} />;
	}

	return <Redirect to={Routes.login} />;
}

function TenantSelector() {
	return (
		<Flex
			column
			gap="xl"
			align="center"
			justify="center"
			style={{ height: "100vh", width: "100vw" }}
		>
			<Text size="lg" weight="medium">
				Select an organization
			</Text>
			<Flex column gap="md" style={{ width: 200 }}>
				{tenantPickerOrder().map((slug) => (
					<Button
						key={slug}
						variant="outline"
						onPress={() => {
							window.location.href = urlForTenantSlug(slug);
						}}
					>
						{TENANT_DISPLAY_NAMES[slug]}
					</Button>
				))}
			</Flex>
		</Flex>
	);
}

function ProtectedRoute<
	T extends DefaultParams | undefined = undefined,
	RoutePath extends PathPattern = PathPattern,
>(routeProps: RouteProps<T, RoutePath>) {
	const user = useLoggedInUser();

	return (
		<Route<T, RoutePath> {...routeProps}>
			{user ? routeProps.children : <Redirect to={Routes.login} />}
		</Route>
	);
}

function AppRoutes() {
	const tenant = getTenantSlug();

	if (!tenant) {
		return <TenantSelector />;
	}

	return (
		<Switch>
			<Route path={RoutePattern.login}>
				<Login />
			</Route>
			<Route path={RoutePattern.error}>
				<ErrorPage />
			</Route>
			<ProtectedRoute path={RoutePattern.admin}>
				<AdminOnly fallback={<Redirect to={Routes.taskList} />}>
					<AdminRoute />
				</AdminOnly>
			</ProtectedRoute>
			<ProtectedRoute path={RoutePattern.uikit}>
				<Flex flex overflow="hidden">
					<Flex style={{ width: "25%" }}>
						<LeftPane />
					</Flex>
					<Flex
						column
						background="sand2"
						overflow="y"
						style={{ width: "75%" }}
						role="main"
						aria-label="Main content"
					>
						<UIKitRoute />
					</Flex>
				</Flex>
			</ProtectedRoute>
			<ProtectedRoute path={RoutePattern.trainingGuide}>
				<Flex flex overflow="hidden">
					<Flex style={{ width: "25%" }}>
						<LeftPane />
					</Flex>
					<Flex
						column
						background="sand2"
						overflow="y"
						style={{ width: "75%" }}
						role="main"
						aria-label="Main content"
					>
						<TrainingGuideRoute />
					</Flex>
				</Flex>
			</ProtectedRoute>
			<ProtectedRoute path={RoutePattern.taskList}>
				<Flex flex overflow="hidden">
					<Flex style={{ width: "25%" }}>
						<LeftPane />
					</Flex>
					<Flex
						column
						align="center"
						justify="center"
						background="sand2"
						style={{ width: "50%" }}
						role="main"
						aria-label="Main content"
					>
						<Text size="md" light role="heading" aria-level={1}>
							Select a task to see details
						</Text>
					</Flex>
					<Flex
						column
						background="sand1"
						borderLeftWidth="md"
						borderColor="sandA3"
						padding="xl"
						gap="lg"
						style={{ width: "25%" }}
					></Flex>
				</Flex>
			</ProtectedRoute>
			<ProtectedRoute path={RoutePattern.taskDetail}>
				{({ taskId }) => (
					<Flex flex overflow="hidden">
						<Flex style={{ width: "25%" }}>
							<LeftPane />
						</Flex>
						<Flex
							column
							background="sand2"
							overflow="y"
							style={{ width: "50%" }}
							role="main"
							aria-label="Main content"
						>
							<TaskDetailsPane key={taskId} taskId={tag(taskId)} />
						</Flex>
						<Flex
							column
							background="sand1"
							borderLeftWidth="md"
							borderColor="sandA3"
							overflow="y"
							style={{ width: "25%" }}
						>
							<AssistantPane key={taskId} taskId={tag(taskId)} />
						</Flex>
					</Flex>
				)}
			</ProtectedRoute>
			<Route path={RoutePattern.root}>
				<Index />
			</Route>
		</Switch>
	);
}

export function App() {
	const basePath = getBasePath();

	return (
		<Router base={basePath}>
			<Providers>
				<SyncErrorHandler />
				<ThemedToaster />
				{import.meta.env.MODE === "development" && <Agentation />}
				<Flex
					column
					background="sand1"
					style={{ height: "100vh", width: "100vw" }}
				>
					<DemoControlBar />
					<Flex flex overflow="hidden">
						<AppRoutes />
					</Flex>
				</Flex>
			</Providers>
		</Router>
	);
}
